import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useStores } from '@/components/StoreProvider'

import { PRIVACY_POLICY_URL } from './constants'

type CategoryRowProps = {
  id: string
  title: string
  description: string
  checked: boolean
  onChange?: (checked: boolean) => void
  lockedLabel?: string
}

const CategoryRow = ({ id, title, description, checked, onChange, lockedLabel }: CategoryRowProps) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 p-4">
    <div className="min-w-0">
      <label htmlFor={id} className="text-sm font-semibold text-gray-900">
        {title}
      </label>
      <p id={`${id}-description`} className="mt-1 text-sm leading-relaxed text-gray-600">
        {description}
      </p>
    </div>
    <div className="flex shrink-0 items-center gap-2 pt-0.5">
      {lockedLabel && <span className="text-xs text-gray-500">{lockedLabel}</span>}
      <Switch id={id} aria-describedby={`${id}-description`} checked={checked} disabled={!onChange} onCheckedChange={onChange} className="data-[state=checked]:bg-[var(--color-primary)]" />
    </div>
  </div>
)

/** Per-category choices. Opened from the banner and from any "Cookie Preferences" link. */
export const CookiePreferences = observer(() => {
  const { t } = useTranslation()
  const { consentStore } = useStores()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const open = consentStore.preferencesOpen

  const [functional, setFunctional] = useState(false)
  const [analytics, setAnalytics] = useState(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      setFunctional(consentStore.functional)
      setAnalytics(consentStore.analytics)
      if (!dialog.open) dialog.showModal()
    } else if (dialog.open) {
      dialog.close()
    }
  }, [open, consentStore])

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="cookie-preferences-title"
      onClose={() => consentStore.closePreferences()}
      onClick={(e) => e.target === e.currentTarget && consentStore.closePreferences()}
      className="m-auto w-[calc(100%-2rem)] max-w-lg rounded-xl bg-white p-0 shadow-2xl backdrop:bg-black/50"
    >
      <div className="flex max-h-[85vh] flex-col">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h2 id="cookie-preferences-title" className="text-lg font-semibold text-gray-900">
              {t('consent.preferences.title')}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              {t('consent.preferences.intro')}{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--color-primary)] underline underline-offset-2">
                {t('consent.privacy_policy')}
              </a>
            </p>
          </div>
          <Button variant="ghost" size="icon" aria-label={t('consent.close')} onClick={() => consentStore.closePreferences()}>
            <X />
          </Button>
        </div>

        <div className="flex flex-col gap-3 overflow-y-auto px-6 py-5">
          <CategoryRow
            id="consent-necessary"
            title={t('consent.categories.necessary.title')}
            description={t('consent.categories.necessary.description')}
            checked
            lockedLabel={t('consent.always_on')}
          />
          <CategoryRow
            id="consent-functional"
            title={t('consent.categories.functional.title')}
            description={t('consent.categories.functional.description')}
            checked={functional}
            onChange={setFunctional}
          />
          <CategoryRow id="consent-analytics" title={t('consent.categories.analytics.title')} description={t('consent.categories.analytics.description')} checked={analytics} onChange={setAnalytics} />
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={() => consentStore.rejectAll()}>
            {t('consent.reject_all')}
          </Button>
          <Button variant="ghost" onClick={() => consentStore.acceptAll()}>
            {t('consent.accept_all')}
          </Button>
          <Button variant="default" onClick={() => consentStore.save({ functional, analytics })}>
            {t('consent.save')}
          </Button>
        </div>
      </div>
    </dialog>
  )
})
CookiePreferences.displayName = 'CookiePreferences'
